import * as React from 'react';

import PropTypes from 'prop-types';

import {FunnelArrowDivider} from './funnel-arrow-divider';
import {FunnelCloseStatus} from './funnel-close-status';
import {FunnelCloseTotals} from './funnel-close-totals';
import {FunnelNumberLabel} from './funnel-number-label';
import {RectangleView} from './rectangle-view';

export class FunnelSection extends React.Component {
    static propTypes = {
        advancedLeads: PropTypes.object.isRequired,
        addedLeads: PropTypes.object.isRequired,
        regressedLeads: PropTypes.object.isRequired,
        cancelledLeads: PropTypes.object.isRequired,
        closedLeads: PropTypes.object.isRequired,
        currentLeads: PropTypes.object.isRequired,
        lostLeads: PropTypes.object.isRequired,
        previousLeads: PropTypes.object.isRequired,
        reassignedLeads: PropTypes.object.isRequired,
        totalLeads: PropTypes.object.isRequired,
        wonLeads: PropTypes.object.isRequired,
        name: PropTypes.string.isRequired,
        isFirstSection: PropTypes.bool.isRequired,
        isLastSection: PropTypes.bool.isRequired,
        isBlank: PropTypes.bool.isRequired,
        maxValue: PropTypes.number.isRequired,
        averageTime: PropTypes.object,
        stageNum: PropTypes.number,

        // Controls whether bars are shown for reassigned leads
        showReassigned: PropTypes.bool.isRequired,
        displayType: PropTypes.oneOf(['count', 'amount']).isRequired,
    };

    constructor(props) {
        super(props);
        this.state = {
            maxRectangleWidth: 5,
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.getMaxRectangleWidth);
        this.getMaxRectangleWidth();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.getMaxRectangleWidth);
    }

    render() {
        const {
            advancedLeads,
            addedLeads,
            averageTime,
            regressedLeads,
            cancelledLeads,
            currentLeads,
            lostLeads,
            previousLeads,
            reassignedLeads,
            totalLeads,
            closedLeads,
            wonLeads,
            name,
            isFirstSection,
            isLastSection,
            showReassigned,
            displayType,
            maxValue,
            stageNum,
        } = this.props;

        return (
            <div className='ui-funnel-report__funnel-section' id={`funnel-section-${stageNum}`}>
                <div className='ui-funnel-section__header'>
                    <div className='ui-funnel-section__header__stage-name' title={name}>
                        <span>{name}</span>
                    </div>
                </div>
                <div className='ui-funnel-section__main'>
                    {isFirstSection ? (
                        <div className='ui-funnel-section__transition ui-funnel-section__transition--added'>
                            <FunnelArrowDivider
                                json={addedLeads}
                                displayType={displayType}
                                isAdded={true}
                                isBlank={this.props.isBlank}
                            />
                        </div>
                    ) : undefined}
                    <div className='ui-funnel-section__main__total-leads-container'>
                        <RectangleView
                            maxSize={maxValue}
                            maxPixelWidth={this.state.maxRectangleWidth}
                            htmlUrl={this.props.isBlank ? '' : totalLeads.htmlUrl}
                            size={this.props.isBlank ? 5 : totalLeads[displayType].value}
                        >
                            {this.props.isBlank ? undefined : (
                                <div className='ui-funnel-section__main__total-leads-label'>
                                    <FunnelNumberLabel
                                        json={totalLeads}
                                        isBlank={this.props.isBlank}
                                        displayType={displayType}
                                    />
                                    {displayType === 'count' ? (
                                        <span> leads</span>
                                    ) : (
                                        <span> value</span>
                                    )}
                                </div>
                            )}
                        </RectangleView>
                        <a
                            href={previousLeads.htmlUrl}
                            className='ui-funnel-section__meta ui-funnel-section__meta--previous ui-funnel-section-tooltip-meta'
                            data-title='Leads that were in this stage at the beginning of the specified date range'
                        >
                            <FunnelNumberLabel
                                isBlank={this.props.isBlank}
                                json={previousLeads}
                                isInline={true}
                                displayType={displayType}
                            />
                            {this.props.isBlank ? undefined : <span> already</span>}
                        </a>
                        <a
                            href={currentLeads.htmlUrl}
                            className='ui-funnel-section__meta ui-funnel-section__meta--current ui-funnel-section-tooltip-meta'
                            data-title='Leads that were still in this stage at the end of the specified date range'
                        >
                            <FunnelNumberLabel
                                isBlank={this.props.isBlank}
                                json={currentLeads}
                                isInline={true}
                                displayType={displayType}
                            />
                            {this.props.isBlank ? undefined : <span> remain</span>}
                        </a>
                    </div>
                    {this.props.isBlank || !averageTime
                        ? undefined
                        : this.renderAverageTimeInStage(averageTime)}
                    {!isLastSection ? (
                        <div className='ui-funnel-section__transition ui-funnel-section__transition--advanced'>
                            <FunnelArrowDivider
                                json={advancedLeads}
                                displayType={displayType}
                                isAdded={false}
                                isBlank={this.props.isBlank}
                            />
                        </div>
                    ) : undefined}
                    {regressedLeads.count.value > 0 ? (
                        <div className='ui-funnel-report__total-regressed-container'>
                            <div className='ui-funnel-report__total-regressed__triangle' />
                            <div className='ui-funnel-report__total-regressed__value'>
                                <FunnelNumberLabel
                                    isBlank={this.props.isBlank}
                                    json={regressedLeads}
                                    isInline={true}
                                    displayType={displayType}
                                />
                                {this.props.isBlank ? undefined : <span> reversed</span>}
                            </div>
                        </div>
                    ) : undefined}
                </div>
                <div className='ui-funnel-section__main__total-closed-container'>
                    <FunnelCloseTotals
                        closedLeads={closedLeads}
                        isBlank={this.props.isBlank}
                        displayType={displayType}
                    />
                </div>
                <div className='ui-funnel-section__main__closed-container'>
                    <FunnelCloseStatus
                        wonLeads={wonLeads}
                        lostLeads={lostLeads}
                        isBlank={this.props.isBlank}
                        cancelledLeads={cancelledLeads}
                        reassignedLeads={reassignedLeads}
                        showReassigned={showReassigned}
                        displayType={displayType}
                    />
                </div>
            </div>
        );
    }

    renderAverageTimeInStage(averageTime) {
        return (
            <div className='ui-funnel-section__average-time'>
                Average time in stage: {averageTime.formatted} {averageTime.unit}
            </div>
        );
    }

    getMaxRectangleWidth = () => {
        const mainFunnelSectionWidth = $('.ui-funnel-report')
            .find('.ui-funnel-report__funnel-section')
            .width();

        // Equals closedTotals(130) + closedStatus (320) + padding(20) + border(2)
        const width = this.props.isBlank ? 10 : mainFunnelSectionWidth - 472;
        this.setState({maxRectangleWidth: width});
    };
}
